<template>
  <div class="container-fluid">
    <div
      class="row tw-relative tw-z-30 tw-border-b tw-border-b-gray-200 md:tw-sticky md:tw-top-0"
      data-testid="sticky-filters-bar"
    >
      <PropertiesSearchFilters
        ref="propertySearchFilters"
        :shortlist-mode-active="shortlistActive"
        :enable-marketplace-grid-split-maps="enableMarketplaceGridSplitMaps"
        :enable-private-halls-filter="enablePrivateHallsFilter"
        :enable-spare-room-functionality="enableSpareRoomFunctionality"
        :enable-local-filter-counts="enableLocalFilterCounts"
        :enable-student-enquiries-cta="
          enableStudentEnquiriesCta && !housemateGroupsExperienceEnabled
        "
        :areas="areas"
        :cities="cities"
        :current-city-slug="currentCitySlug"
        :current-landlord="currentLandlord"
        :current-university-slug="currentUniversitySlug"
        :current-area-slug="currentAreaSlug"
        :current-bedrooms="currentBedrooms"
        :current-bathrooms="currentBathrooms"
        :current-type="currentType"
        :current-max-price="currentMaxPrice"
        :current-enquired-properties="enquiredProperties"
        :landlord-query="landlordQuery"
        :max-cheapest-room-price="maxCheapestRoomPrice"
        :max-price-selected="maxPriceSelected"
        :min-cheapest-room-price="minCheapestRoomPrice"
        :property-count="displayedPropertyCount"
        :results-count-loading="resultsCountLoading"
        :reloading="reloading"
        :rooms="rooms"
        :types="types"
        :universities="universities"
        :title-results-count="titleResultsCount"
        :use-exact-room-counts="useExactRoomCounts"
        :has-enquired-properties="hasEnquiredProperties"
        :enable-housemate-groups="useHousemateGroupsSearchExperience"
        :enable-property-type-filters="enablePropertyTypeFilters"
        @set-current-area-slug="setCurrentAreaSlug"
        @set-current-bathrooms="setCurrentBathrooms"
        @set-current-bedrooms="setCurrentBedrooms"
        @set-current-city-slug="setCurrentCitySlug"
        @set-current-max-price="setCurrentMaxPrice"
        @set-current-min-price="setCurrentMinPrice"
        @set-current-type="setCurrentType"
        @set-current-university-slug="setCurrentUniversitySlug"
        @set-max-price-selected="setMaxPriceSelected"
        @set-min-price-selected="setMinPriceSelected"
        @set-enquired-properties="setEnquiredProperties"
        @desktop-property-filters-open="handleDesktopPropertyFiltersOpen"
        @desktop-property-filters-close="handleDesktopPropertyFiltersClose"
        @filter-modal-close="handleModalClose"
        @apply-inline-property-type-filter="handleInlinePropertyTypeFilter"
        @toggle-map-is-open="toggleMapIsOpen"
      >
        <template slot="shortlist-list-selector-desktop">
          <ShortlistListsSelector
            v-if="showShortlistListsSelector && isDesktopViewport"
            :location="shortlistListLocation"
            :enable-property-type-filters="enablePropertyTypeFilters"
            @selection-change="handleShortlistListSelectionChange"
            @load-error="handleShortlistListSelectionError"
          />
        </template>
        <template slot="shortlist-list-selector-mobile">
          <ShortlistListsSelector
            v-if="showShortlistListsSelector && !isDesktopViewport"
            :location="shortlistListLocation"
            :enable-property-type-filters="enablePropertyTypeFilters"
            @selection-change="handleShortlistListSelectionChange"
            @load-error="handleShortlistListSelectionError"
          />
        </template>
      </PropertiesSearchFilters>
    </div>
    <div
      v-if="shouldRenderPropertiesSearchMain"
      id="propertiesSearchMain"
      :class="[
        'row tw-bg-white max-md:tw-overflow-x-clip',
        keepPropertyTypeFiltersVisibleOnNoResults
          ? 'tw-px-0 md:tw-px-[24px]'
          : 'tw-px-[20px] md:tw-px-[24px]',
      ]"
    >
      <!-- SET LD flag to swap between new and old component-->
      <PropertiesSearchMainMap
        v-if="useStandardSearchExperience"
        ref="propertiesSearchMain"
        :shortlist-active="shortlistActive"
        :shortlist-read-only="isSharedShortlist"
        :shortlist-title="selectedShortlistTitle"
        :shortlist-results-loaded="shortlistResultsLoaded"
        :enable-shortlist-in-search="useShortlistInSearchExperience"
        :enable-new-map="enableNewMap"
        :enable-marketplace-grid-split-maps="enableMarketplaceGridSplitMaps"
        :enable-updated-featured-properties="enableUpdatedFeaturedProperties"
        :enable-updated-property-tile="enableUpdatedPropertyTile"
        :desktop-property-filters-open="desktopPropertyFiltersOpen"
        :show-upcoming-properties-banner="
          shortlistActive ? false : showUpcomingPropertiesBanner
        "
        :show-contents-insurance-banner="effectiveShowContentsInsuranceBanner"
        :admin="admin"
        :admin-first-name="adminFirstName"
        :areas="areas"
        :bottom-of-the-page="bottomOfThePage"
        :cities="cities"
        :current-area-slug="currentAreaSlug"
        :current-city-slug="currentCitySlug"
        :current-sort="currentSort"
        :current-sort-title="currentSortTitle"
        :current-sort-title-short="currentSortTitleShort"
        :current-type="currentType"
        :current-url="currentUrl"
        :enable-private-halls-filter="enablePrivateHallsFilter"
        :enable-spare-room-functionality="enableSpareRoomFunctionality"
        :enable-property-type-filters="enablePropertyTypeFilters"
        :display-nearby="shortlistActive ? false : displayNearby"
        :hide-nearby="hideNearby"
        :initial-page="initialPage"
        :loading="loading"
        :map-count-zero="mapCountZero"
        :map-move-up="mapMoveUp"
        :nearby="shortlistActive ? [] : nearby"
        :number-of-pages="displayedNumberOfPages"
        :page="page"
        :paginate="paginate"
        :properties="displayedProperties"
        :properties-count="displayedProperties.length"
        :properties-has-more-pages="
          shortlistActive ? false : propertiesHasMorePages
        "
        :properties-slider-slides="propertiesSliderSlides"
        :property-count="displayedPropertyCount"
        :screen-key="screenKey"
        :selected-view="selectedView"
        :shortlist-nudge-enabled="shortlistNudgeEnabled"
        :show-spare-rooms-one-bed-nudge="
          shortlistActive ? false : showSpareRoomsOneBedNudge
        "
        :spare-rooms-one-bed-overlay-count="spareRoomsOneBedOverlayCount"
        :spare-rooms-one-bed-overlay-apply-url="spareRoomsOneBedOverlayApplyUrl"
        :show-nearby="shortlistActive ? 0 : showNearbyValue"
        :show-no-results="showNoResults"
        :sort-options="sortOptions"
        :reloading="shortlistActive ? shortlistLoading : reloading"
        :title="
          shortlistActive
            ? `${displayedPropertyCount} ${
                displayedPropertyCount === 1 ? 'property' : 'properties'
              }`
            : title
        "
        :title-results-count="titleResultsCount"
        :window-height="windowHeight"
        :enquired-properties="enquiredProperties"
        :map-view-pill-position-test="mapViewPillPositionTest"
        @set-page="setPage"
        @set-current-sort="setCurrentSort"
        @set-current-type="setCurrentType"
        @show-more-properties="showMoreProperties"
        @set-selected-view="setSelectedViewAndSync"
        @toggle-map-is-open="toggleMapIsOpen"
        @remove-from-shortlist="handleShortlistPropertyRemoval"
      ></PropertiesSearchMainMap>

      <!-- SET LD flag to swap between new and old component-->
      <PropertiesSearchMainHouseMateGroup
        v-if="useHousemateGroupsSearchExperience"
        ref="propertiesSearchMain"
        :enable-new-map="enableNewMap"
        :enable-marketplace-grid-split-maps="enableMarketplaceGridSplitMaps"
        :enable-updated-featured-properties="enableUpdatedFeaturedProperties"
        :enable-updated-property-tile="enableUpdatedPropertyTile"
        :show-upcoming-properties-banner="showUpcomingPropertiesBanner"
        :show-contents-insurance-banner="effectiveShowContentsInsuranceBanner"
        :admin="admin"
        :admin-first-name="adminFirstName"
        :areas="areas"
        :bottom-of-the-page="bottomOfThePage"
        :cities="cities"
        :all-universities="allUniversities"
        :universities="universities"
        :current-area-slug="currentAreaSlug"
        :current-city-slug="currentCitySlug"
        :current-bedrooms="currentBedrooms"
        :current-bathrooms="currentBathrooms"
        :current-min-price="currentMinPrice"
        :current-max-price="currentMaxPrice"
        :min-price-selected="minPriceSelected"
        :max-price-selected="maxPriceSelected"
        :current-sort="currentSort"
        :current-sort-title="currentSortTitle"
        :current-sort-title-short="currentSortTitleShort"
        :current-type="currentType"
        :current-landlord="currentLandlord"
        :current-url="currentUrl"
        :enable-private-halls-filter="enablePrivateHallsFilter"
        :enable-spare-room-functionality="enableSpareRoomFunctionality"
        :enable-property-type-filters="enablePropertyTypeFilters"
        :display-nearby="displayNearby"
        :hide-nearby="hideNearby"
        :initial-page="initialPage"
        :loading="loading"
        :map-count-zero="mapCountZero"
        :map-move-up="mapMoveUp"
        :nearby="nearby"
        :number-of-pages="numberOfPages"
        :page="page"
        :paginate="paginate"
        :properties="properties"
        :properties-count="propertiesCount"
        :properties-has-more-pages="propertiesHasMorePages"
        :properties-slider-slides="propertiesSliderSlides"
        :property-count="propertyCount"
        :screen-key="screenKey"
        :shortlist-nudge-enabled="shortlistNudgeEnabled"
        :show-spare-rooms-one-bed-nudge="showSpareRoomsOneBedNudge"
        :spare-rooms-one-bed-overlay-count="spareRoomsOneBedOverlayCount"
        :spare-rooms-one-bed-overlay-apply-url="spareRoomsOneBedOverlayApplyUrl"
        :show-nearby="showNearbyValue"
        :show-no-results="showNoResults"
        :sort-options="sortOptions"
        :reloading="reloading"
        :title="title"
        :title-results-count="titleResultsCount"
        :window-height="windowHeight"
        :enquired-properties="enquiredProperties"
        :is-logged-in-student="isLoggedInStudent"
        :use-exact-room-counts="useExactRoomCounts"
        :enable-housemate-groups="housemateGroupsExperienceEnabled"
        :has-enquired-properties="hasEnquiredProperties"
        :housemate-group-show-shortlist-tab="housemateGroupShowShortlistTab"
        :show-housemate-group-tab="showHousemateGroupTab"
        :open-housemate-group-tab="openHousemateGroupTab"
        :desktop-property-filters-open="desktopPropertyFiltersOpen"
        :show-mobile-property-type-filters-only="
          keepPropertyTypeFiltersVisibleOnNoResults
        "
        @set-page="setPage"
        @set-current-sort="setCurrentSort"
        @set-current-type="setCurrentType"
        @set-enquired-properties="setEnquiredPropertiesFromTab"
        @show-more-properties="showMoreProperties"
        @toggle-map-is-open="toggleMapIsOpen"
      ></PropertiesSearchMainHouseMateGroup>
    </div>
    <div v-if="!enquiredProperties">
      <div v-if="propertiesCount && propertyCount < propertyLessThat">
        <SignUpNew
          :city="currentCity"
          :sign-up-type="signUpType"
          :type="currentTypeAsString"
          outer-classes="sign-up-results"
        ></SignUpNew>
      </div>
      <div
        v-else-if="
          !propertiesCount &&
          (!!currentArea ||
            !!currentBathrooms ||
            !!currentBedrooms ||
            !!currentMaxPrice ||
            !!currentMinPrice ||
            !!currentUniversity)
        "
      >
        <SignUpNew
          :city="currentCity"
          :sign-up-type="signUpType"
          :type="currentTypeAsString"
          outer-classes="sign-up-filters"
        ></SignUpNew>
      </div>
      <div
        v-else-if="
          !propertiesCount &&
          !currentArea &&
          !currentBathrooms &&
          !currentBedrooms &&
          !currentMaxPrice &&
          !currentMinPrice &&
          !currentUniversity
        "
      >
        <SignUpNew
          :city="currentCity"
          :landlord="currentLandlord"
          :sign-up-type="signUpCity"
          :type="currentTypeAsString"
          :letting-season-end="lettingSeasonEnd"
          :letting-season-next="lettingSeasonNext"
          outer-classes="sign-up-city"
        >
        </SignUpNew>
      </div>
    </div>
    <ShortlistNotification v-if="useShortlistNotificationExperience" />
  </div>
</template>

<script>
/* global gtag */
import axios from 'axios';
import debounce from 'lodash/debounce';
import { getCurrentPath } from '@js/utilities';
import PropertiesSearchFilters from '@components/PropertiesSearchFilters';
import { getDefaultPropertyTypeFilters } from '@components/Property/MobileSearchFilters/helpers.ts';
import api from '@js/api/authenticatedApi';
import { usePropertiesStore } from '@js/stores/properties';
import { useMapStore } from '@js/stores/map/mapStore';
import { useSearchModeStore } from '@js/stores/search/searchModeStore';
import PropertiesSearchMainMap from '@components/properties-search/components/PropertiesSearchMainMap.vue';
import PropertiesSearchMainHouseMateGroup from '@components/properties-search/components/PropertiesSearchMainHouseMateGroup.vue';
import ShortlistListsSelector from '@components/Student/ShortlistListsSelector.vue';
import SignUpNew from '@components/SignUpNew.vue';
import usePropertyFilters from '@/js/composables/usePropertyFilters';
import { useStudentStore } from '@js/stores/student/student';
import {
  buildShortlistFilters,
  getFilteredShortlist,
} from '@components/Student/shortlistFilters';
import ShortlistNotification from '@components/Property/ShortlistNotification.vue';

import shortlistPageRestore from '@components/Student/helpers/shortlistPageRestore';

const ENQUIRED_PROPERTIES_URL = '/student-accommodation?enquired-properties=1';
const RECENTLY_ADDED_HASH = 'recently-added';

const GRID_SPLIT_VIEWS = ['grid_view', 'split_view', 'grid', 'split'];

function normalizeMarketplaceView(view) {
  if (view === 'grid') return 'grid_view';
  if (view === 'split') return 'split_view';
  return view || 'list_view';
}

function resolveMarketplaceSelectedView(view, canShowGridSplit) {
  const normalized = normalizeMarketplaceView(view);

  if (!canShowGridSplit) {
    if (
      GRID_SPLIT_VIEWS.includes(normalized) ||
      GRID_SPLIT_VIEWS.includes(view)
    ) {
      return 'list_view';
    }
    return normalized;
  }

  // Full-screen map stays map_view. List and unknown views become Split.
  if (normalized === 'map_view') {
    return 'map_view';
  }

  if (normalized === 'list_view') {
    return 'split_view';
  }

  if (normalized === 'grid_view' || normalized === 'split_view') {
    return normalized;
  }

  return 'split_view';
}

export default {
  name: 'PropertiesSearch2024',
  components: {
    PropertiesSearchMainHouseMateGroup,
    ShortlistListsSelector,
    ShortlistNotification,
    SignUpNew,
    PropertiesSearchMainMap,
    PropertiesSearchFilters,
  },
  mixins: [shortlistPageRestore],
  provide() {
    return {
      shortlistNotificationsEnabled: this.useShortlistNotificationExperience,
    };
  },
  props: {
    enableNewMap: {
      default: false,
      type: Boolean,
    },
    enableMarketplaceGridSplitMaps: {
      default: false,
      type: Boolean,
    },
    enableUpdatedFeaturedProperties: {
      default: false,
      type: Boolean,
    },
    enableUpdatedPropertyTile: {
      default: false,
      type: Boolean,
    },
    enablePrivateHallsFilter: {
      default: false,
      type: Boolean,
    },
    enableSpareRoomFunctionality: {
      default: false,
      type: Boolean,
    },
    enablePropertyTypeFilters: {
      default: false,
      type: Boolean,
    },
    enableLocalFilterCounts: {
      default: false,
      type: Boolean,
    },
    showUpcomingPropertiesBanner: {
      default: false,
      type: Boolean,
    },
    contentsInsuranceEnabled: {
      default: false,
      type: Boolean,
    },
    admin: {
      default: false,
      type: Boolean,
    },
    adminFirstName: {
      default: null,
      type: String,
    },
    analytics: {
      default: 'ga',
      type: String,
    },
    areas: {
      default: function () {
        return [];
      },
      type: Array,
    },
    cities: {
      default: function () {
        return [];
      },
      type: Array,
    },
    currentLandlord: {
      default: null,
      type: [Object, String],
    },
    sortOptions: {
      default: function () {
        return {};
      },
      type: Object,
    },
    currentUrl: {
      default: null,
      type: String,
    },
    defaultZoomLevel: {
      default: 12,
      type: Number,
    },
    enableSubscribeFormOnEmptyCities: {
      default: true,
      type: Boolean,
    },
    enableSubscribeFormOnEmptyLettingAgents: {
      default: true,
      type: Boolean,
    },
    initialCompleteUrl: {
      default: null,
      type: String,
    },
    initialCitySlug: {
      default: null,
      type: String,
    },
    initialCurrentUniversitySlug: {
      default: null,
      type: String,
    },
    initialCurrentAreaSlug: {
      default: null,
      type: String,
    },
    initialCurrentBedroom: {
      default: function () {
        return {};
      },
      type: [Object, String],
    },
    initialCurrentBedrooms: {
      default: null,
      type: [Number, String],
    },
    initialCurrentBathrooms: {
      default: null,
      type: [Number, String],
    },
    initialCurrentMinPrice: {
      default: null,
      type: [Number, String],
    },
    initialCurrentMaxPrice: {
      default: null,
      type: [Number, String],
    },
    initialCurrentSort: {
      default: null,
      type: String,
    },
    initialCurrentType: {
      default: null,
      type: String,
    },
    initialHideNearby: {
      default: false,
      type: Boolean,
    },
    initialMapIsOpen: {
      default: false,
      type: Boolean,
    },
    initialMaxCheapestRoomPrice: {
      default: null,
      type: [Number, String],
    },
    initialNearby: {
      default: function () {
        return [];
      },
      type: Array,
    },
    initialPage: {
      default: null,
      type: Number,
    },
    initialPaginate: {
      default: 0,
      type: Number,
    },
    initialProperties: {
      default: function () {
        return [];
      },
      type: Array,
    },
    initialPropertiesHasMorePages: {
      default: false,
      type: Boolean,
    },
    initialPropertyCount: {
      default: 0,
      type: Number,
    },
    initialUniversityQuery: {
      default: null,
      type: String,
    },
    initialSelectedView: {
      default: 'list_view',
      type: String,
    },
    initialSignUpData: {
      default: null,
      type: [Object, String],
    },
    initialTitle: {
      default: null,
      type: String,
    },
    initialShortlistTitle: {
      default: '',
      type: String,
    },
    initialTitleResultsCount: {
      default: null,
      type: [Number, String],
    },
    initialEnquiredProperties: {
      default: false,
      type: Boolean,
    },
    initialShowSpareRoomsOneBedNudge: {
      default: false,
      type: Boolean,
    },
    initialSearchUsesAlgolia: {
      default: false,
      type: Boolean,
    },
    initialBlendedSearch: {
      default: false,
      type: Boolean,
    },
    initialSpareRoomsOneBedOverlayCount: {
      default: 0,
      type: Number,
    },
    initialSpareRoomsOneBedOverlayApplyUrl: {
      default: '',
      type: String,
    },
    minCheapestRoomPrice: {
      default: null,
      type: [Number, String],
    },
    landlordCity: {
      default: null,
      type: [Object, String],
    },
    landlordQuery: {
      default: null,
      type: [Number, String],
    },
    rooms: {
      default: function () {
        return [];
      },
      type: Array,
    },
    initialShortlistNudgeEnabled: {
      default: false,
      type: Boolean,
    },
    showNearby: {
      default: 3,
      type: Number,
    },
    types: {
      default: function () {
        return [];
      },
      type: Array,
    },
    universities: {
      default: function () {
        return [];
      },
      type: Array,
    },
    allUniversities: {
      default: function () {
        return [];
      },
      type: Array,
    },
    clusterMarkersEnabled: {
      default: false,
      type: Boolean,
    },
    propertyIncentives: {
      default: false,
      type: Boolean,
    },
    enableNewListingPage: {
      default: true,
      type: Boolean,
    },
    lettingSeasonEnd: {
      default: null,
      type: Number,
    },
    lettingSeasonNext: {
      default: null,
      type: Number,
    },
    propertyLessThat: {
      default: null,
      type: Number,
    },
    useExactRoomCounts: {
      type: Boolean,
      default: false,
    },
    enableStudentEnquiriesCta: {
      type: Boolean,
      default: false,
    },
    lastSearchUrl: {
      default: null,
      type: String,
    },
    hasEnquiredProperties: {
      type: Boolean,
      default: false,
    },
    isLoggedInStudent: {
      default: false,
      type: Boolean,
    },
    housemateGroupCreateLimit: {
      default: null,
      type: Number,
    },
    enableHousemateGroups: {
      default: null,
      type: Boolean,
    },
    enableShortlistNotificationAnimation: {
      default: false,
      type: Boolean,
    },
    enableShortlistInSearch: {
      default: false,
      type: Boolean,
    },
    studentId: {
      default: null,
      type: Number,
    },
    studentDefaultHousemateGroupId: {
      default: null,
      type: Number,
    },
    housemateGroupInviteInitiated: {
      default: false,
      type: Boolean,
    },
    mapViewPillPositionTest: {
      default: 'control',
      type: String,
    },
    housemateGroupShowShortlistTab: {
      default: false,
      type: Boolean,
    },
    showHousemateGroupTab: {
      default: false,
      type: Boolean,
    },
    openHousemateGroupTab: {
      default: false,
      type: Boolean,
    },
  },

  data() {
    const studentStore = useStudentStore();
    const shortlistActive = (() => {
      if (!this.enableShortlistInSearch) return false;

      try {
        return (
          new URLSearchParams(window.location.search).get('tab') === 'shortlist'
        );
      } catch {
        return false;
      }
    })();
    const shortlistHash = (() => {
      if (!shortlistActive) return null;

      try {
        return new URLSearchParams(window.location.search).get('shortlistHash');
      } catch {
        return null;
      }
    })();
    const enquiredPropertiesFromUrl = (() => {
      try {
        const params = new URLSearchParams(window.location.search);
        return (
          params.get('enquired-properties') === '1' ||
          params.get('enquired_properties') === '1'
        );
      } catch {
        return false;
      }
    })();
    const initialIsEnquiries =
      (this.initialEnquiredProperties || enquiredPropertiesFromUrl) &&
      this.isLoggedInStudent &&
      this.hasEnquiredProperties;

    const canShowGridSplit = Boolean(
      this.enableMarketplaceGridSplitMaps &&
        this.enableNewMap &&
        !shortlistActive
    );
    const resolvedInitialSelectedView = resolveMarketplaceSelectedView(
      this.initialSelectedView,
      canShowGridSplit
    );

    return {
      // Google Maps related data properties removed for test variant
      completeURL: this.initialCompleteUrl,
      currentAreaSlug: this.initialCurrentAreaSlug
        ? this.initialCurrentAreaSlug
        : null,
      currentBathrooms: this.initialCurrentBathrooms,
      currentBedroom: this.initialCurrentBedroom,
      currentBedrooms: this.initialCurrentBedrooms,
      currentCitySlug: this.initialCitySlug,
      currentMinPrice: this.initialCurrentMinPrice,
      currentMaxPrice: this.initialCurrentMaxPrice,
      currentScroll: 0,
      currentSort: this.initialCurrentSort,
      currentType: this.initialCurrentType,
      currentUniversitySlug: this.initialCurrentUniversitySlug
        ? this.initialCurrentUniversitySlug
        : null,
      handleDebouncedScroll: null,
      hideNearby: this.initialHideNearby,
      history: [],
      loading: false,
      shouldReloadForDefaultPropertyTypes: false,
      models: [],
      // Google Maps related properties removed for test variant
      mapIsOpen:
        resolvedInitialSelectedView === 'map_view' ||
        (!canShowGridSplit && this.initialMapIsOpen),
      mapWasOpened: false,
      maxCheapestRoomPrice: this.initialMaxCheapestRoomPrice,
      minPriceSelected: false,
      maxPriceSelected: false,
      mapCountZero: false,
      mapMoveUp: false,
      // Google Maps related properties removed for test variant
      nearby: this.initialNearby,
      listingPropertySwipers: [],
      page: this.initialPage,
      paginate: this.initialPaginate,
      properties: this.initialProperties,
      propertiesHasMorePages: this.initialPropertiesHasMorePages,
      propertiesSliderSlides: null,
      propertyCount: this.initialPropertyCount,
      cachedSearchPropertyCount: initialIsEnquiries
        ? null
        : this.initialPropertyCount,
      cachedEnquiriesPropertyCount: initialIsEnquiries
        ? this.initialPropertyCount
        : null,
      cachedSearchTitle: initialIsEnquiries ? null : this.initialTitle,
      cachedSearchTitleResultsCount: initialIsEnquiries
        ? null
        : this.initialTitleResultsCount,
      cachedEnquiriesTitle: initialIsEnquiries ? this.initialTitle : null,
      cachedEnquiriesTitleResultsCount: initialIsEnquiries
        ? this.initialTitleResultsCount
        : null,
      resultsCountLoading: false,
      reloading: false,
      screenKey: this.makeId(5),
      selectedView: resolvedInitialSelectedView,
      // Prior Grid/Split mode when expanding to full-page map (TAC-1105)
      marketplaceViewBeforeFullscreen: null,
      shortTitle: null,
      showNearbyValue: this.showNearby,
      showNoResults: false,
      signUpData: this.initialSignUpData,
      shortlistNudgeEnabled: this.initialShortlistNudgeEnabled,
      shortlistActive,
      shortlistHash,
      activeShortlistList: null,
      activeShortlistProperties: [],
      shortlistLoading: false,
      shortlistResultsLoaded:
        !shortlistActive ||
        (this.enableShortlistInSearch &&
          this.isLoggedInStudent &&
          !shortlistHash),
      showSpareRoomsOneBedNudge: this.initialShowSpareRoomsOneBedNudge,
      blendedSearch: this.initialBlendedSearch,
      spareRoomsOneBedOverlayCount: this.initialSpareRoomsOneBedOverlayCount,
      spareRoomsOneBedOverlayApplyUrl:
        this.initialSpareRoomsOneBedOverlayApplyUrl,
      signUpType: 'signUpResults',
      signUpCity: 'signUpCity',
      swiper: null,
      target: null,
      title: this.initialTitle,
      titleResultsCount: this.initialTitleResultsCount,
      universityQuery: this.initialUniversityQuery,
      enquiredProperties: initialIsEnquiries,
      enquiriesReturnState: null,
      windowHeight: window.innerHeight,
      windowWidth: window.innerWidth,
      // Google Maps related properties removed for test variant
      isMobile: window.screen.width < 992,
      redirectEnquiredProperties: true,
      preserveSearchUrlDuringEnquiriesTab: false,
      prefetchedEnquiriesPage1: null,
      desktopPropertyFiltersOpen: false,
      studentStore,
    };
  },
  computed: {
    canShowMapView() {
      if (this.shortlistActive) return false;

      const currentPath = this.getCurrentPath();
      return Boolean(
        currentPath &&
          currentPath !== '/student-accommodation' &&
          this.enableNewMap
      );
    },
    canShowGridSplitMaps() {
      return Boolean(
        this.canShowMapView && this.enableMarketplaceGridSplitMaps
      );
    },
    housemateGroupsExperienceEnabled() {
      return Boolean(this.enableHousemateGroups);
    },
    useShortlistInSearchExperience() {
      return Boolean(this.enableShortlistInSearch);
    },
    useShortlistNotificationExperience() {
      return (
        this.useShortlistInSearchExperience ||
        this.enableShortlistNotificationAnimation
      );
    },
    useHousemateGroupsSearchExperience() {
      return (
        this.housemateGroupsExperienceEnabled &&
        !this.useShortlistInSearchExperience
      );
    },
    useStandardSearchExperience() {
      return !this.useHousemateGroupsSearchExperience;
    },
    shortlistFilters() {
      return buildShortlistFilters({
        currentBedrooms: this.currentBedrooms,
        currentBathrooms: this.currentBathrooms,
        currentMinPrice: this.currentMinPrice,
        currentMaxPrice: this.currentMaxPrice,
        minPriceSelected: this.minPriceSelected,
        maxPriceSelected: this.maxPriceSelected,
        currentType: this.currentType,
        currentLandlord: this.currentLandlord,
        useExactRoomCounts: this.useExactRoomCounts,
      });
    },
    isSharedShortlist() {
      return Boolean(this.shortlistHash);
    },
    shortlistListLocation() {
      return {
        city_name: this.currentCity?.name || null,
        area_name: this.currentArea?.name || null,
      };
    },
    selectedShortlistTitle() {
      return this.activeShortlistList?.name || this.initialShortlistTitle;
    },
    isDesktopViewport() {
      return this.windowWidth >= 1024;
    },
    showShortlistListsSelector() {
      return (
        this.shortlistActive &&
        this.useShortlistInSearchExperience &&
        this.isLoggedInStudent &&
        !this.isSharedShortlist
      );
    },
    filteredShortlistProperties() {
      if (!this.shortlistActive) return [];

      if (this.isSharedShortlist) {
        void this.studentStore.sharedShortlist;
        return this.studentStore.filterSharedShortlist(this.shortlistFilters);
      }

      if (
        this.useShortlistInSearchExperience &&
        this.isLoggedInStudent &&
        !this.isSharedShortlist
      ) {
        if (!this.activeShortlistList) return this.properties;

        return this.studentStore.filterProperties(
          this.activeShortlistProperties,
          this.shortlistFilters
        );
      }

      return getFilteredShortlist({
        store: this.studentStore,
        filters: this.shortlistFilters,
      });
    },
    displayedProperties() {
      if (!this.shortlistActive) {
        return Array.isArray(this.properties) ? this.properties : [];
      }

      const page = Number(this.page || 1);
      const paginate = Number(
        this.paginate || this.filteredShortlistProperties.length
      );
      const start = Math.max(page - 1, 0) * paginate;

      return this.filteredShortlistProperties.slice(start, start + paginate);
    },
    displayedPropertyCount() {
      if (!this.shortlistActive) return this.propertyCount;

      return !this.shortlistResultsLoaded
        ? this.initialPropertyCount
        : this.filteredShortlistProperties.length;
    },
    displayedNumberOfPages() {
      if (!this.shortlistActive) return this.numberOfPages;

      const paginate = Number(this.paginate || 0);
      return paginate > 0
        ? Math.max(1, Math.ceil(this.displayedPropertyCount / paginate))
        : 1;
    },
    bottomOfThePage() {
      return this.currentScroll + this.windowHeight;
    },
    currentArea() {
      var scope = this;
      return scope.currentAreaSlug
        ? this.areas.filter(function (area) {
            return area.slug === scope.currentAreaSlug;
          })[0]
        : null;
    },
    currentAreaName() {
      return this.currentArea ? this.currentArea.name : null;
    },
    currentCity() {
      var scope = this;
      return scope.currentCitySlug
        ? this.cities.filter(function (city) {
            return city.id === scope.currentCitySlug;
          })[0]
        : null;
    },
    currentCityCoordinates() {
      return this.currentCity
        ? {
            lat: this.currentCity.latitude,
            lng: this.currentCity.longitude,
          }
        : null;
    },
    currentCityName() {
      return this.currentCity ? this.currentCity.name : null;
    },
    currentSortTitle() {
      var scope = this;
      return scope.sortOptions
        ? Object.keys(scope.sortOptions)
            .filter((key) => key === scope.currentSort)
            .reduce((obj, key) => {
              return scope.sortOptions[key].full;
            }, {})
        : null;
    },
    currentSortTitleShort() {
      var scope = this;
      return scope.sortOptions
        ? Object.keys(scope.sortOptions)
            .filter((key) => key === scope.currentSort)
            .reduce((obj, key) => {
              return scope.sortOptions[key].short;
            }, {})
        : null;
    },
    currentUniversity() {
      var scope = this;
      return scope.currentUniversitySlug
        ? this.universities.filter(function (university) {
            return university.id === scope.currentUniversitySlug;
          })[0]
        : null;
    },
    currentUniversityName() {
      return this.currentUniversity ? this.currentUniversity.name : null;
    },
    currentTypeAsString() {
      const types = this.normalizePropertyTypes(this.currentType);

      return types.length > 0 ? types.join(',') : null;
    },
    isPrivateHallsOnlyFilter() {
      const selectedTypes = this.normalizePropertyTypes(this.currentType);
      const structuralTypes = [
        ...new Set(
          selectedTypes.filter(
            (type) =>
              type === 'house' ||
              type === 'apartment' ||
              type === 'private-halls'
          )
        ),
      ];

      return (
        structuralTypes.length === 1 &&
        structuralTypes[0] === 'private-halls' &&
        !selectedTypes.includes('spare-rooms')
      );
    },
    effectiveShowContentsInsuranceBanner() {
      return this.contentsInsuranceEnabled && !this.isPrivateHallsOnlyFilter;
    },
    displayNearby() {
      return (
        !this.hideNearby &&
        Array.isArray(this.nearby) &&
        this.nearby.length !== 0
      );
    },
    isGridOrSplitSelectedView() {
      return GRID_SPLIT_VIEWS.includes(this.selectedView);
    },
    // Google Maps related computed properties removed for test variant
    numberOfPages() {
      const propertyCount = Number(this.propertyCount || 0);
      const paginate = Number(this.paginate || 0);
      if (paginate <= 0) {
        return 1;
      }

      return Math.ceil(propertyCount / paginate);
    },
    propertiesCount() {
      return Array.isArray(this.properties) ? this.properties.length : null;
    },
    keepPropertyTypeFiltersVisibleOnNoResults() {
      return (
        this.housemateGroupsExperienceEnabled &&
        this.enablePropertyTypeFilters &&
        this.windowWidth < 768 &&
        !this.propertiesCount &&
        !!this.currentTypeAsString
      );
    },
    shouldRenderPropertiesSearchMain() {
      return (
        this.shortlistActive ||
        this.propertiesCount ||
        this.enquiredProperties ||
        this.keepPropertyTypeFiltersVisibleOnNoResults
      );
    },
    propertiesSearchTitle() {
      if (this.currentBedroom) {
        if (this.currentBedroom.search_title) {
          return this.currentBedroom.search_title;
        } else {
          return (
            this.currentBedroom.number +
            ' Bedroom Student ' +
            (['1', '2'].includes(this.currentBedroom.number) ? 'Flats/' : '') +
            'Houses in ' +
            this.currentCityName
          );
        }
      } else if (
        this.currentUniversity &&
        !this.universityQuery &&
        !this.currentArea &&
        this.currentUniversity.search_title
      ) {
        return this.returnFilterBarTitle(
          'University',
          this.currentUniversity,
          true
        );
      } else if (
        this.currentArea &&
        (!this.currentUniversity || this.universityQuery) &&
        this.currentArea.search_title
      ) {
        return this.returnFilterBarTitle('Area', this.currentArea, true);
      } else if (
        this.currentCity &&
        !this.currentUniversity &&
        !this.currentArea &&
        this.currentCity.search_title
      ) {
        return this.returnFilterBarTitle('City', this.currentCity, true);
      } else {
        let ret = '';
        if (!this.currentUniversity) {
          ret += 'Compare ';
        }
        ret += 'All Inclusive Student Accommodation';
        if (this.currentUniversity) {
          ret += ' near ' + this.currentUniversityName;
        } else if (this.currentCity) {
          ret += ' in ';

          if (this.currentArea) {
            ret += this.currentAreaName + ', ';
          }

          ret += this.currentCityName;
        }
        return ret;
      }
    },

    propertiesSearchTitleMobile() {
      if (this.currentBedroom) {
        if (this.currentBedroom.search_title) {
          return this.currentBedroom.search_title;
        } else {
          return (
            this.currentBedroom.number +
            ' Bedroom Student ' +
            (['1', '2'].includes(this.currentBedroom.number) ? 'Flats/' : '') +
            'Houses in ' +
            this.currentCityName
          );
        }
      } else if (
        this.currentUniversity &&
        !this.universityQuery &&
        !this.currentArea &&
        this.currentUniversity.search_title
      ) {
        return this.returnFilterBarTitle('University', this.currentUniversity);
      } else if (
        this.currentArea &&
        (!this.currentUniversity || this.universityQuery) &&
        this.currentArea.search_title
      ) {
        return this.returnFilterBarTitle('Area', this.currentArea);
      } else if (
        this.currentCity &&
        !this.currentUniversity &&
        !this.currentArea &&
        this.currentCity.search_title
      ) {
        return this.returnFilterBarTitle('City', this.currentCity);
      } else {
        let ret = '';
        if (!this.currentUniversity) {
          ret += 'Compare ';
        }
        ret += 'All Inclusive Student Accommodation';
        if (this.currentUniversity) {
          ret += ' near ' + this.currentUniversityName;
        } else if (this.currentCity) {
          ret += ' in ';

          if (this.currentArea) {
            ret += this.currentAreaName + ', ';
          }

          ret += this.currentCityName;
        }
        return ret;
      }
    },
  },

  watch: {
    // Google Maps related watchers removed for test variant
    properties: {
      handler: function () {},
      deep: true,
    },
    propertiesCount(newCount) {
      if (newCount === 0) {
        this.signUpType = 'signUpFilters';
      } else {
        this.signUpType = 'signUpResults';
      }
    },
    displayedNumberOfPages(numberOfPages) {
      if (this.shortlistActive && Number(this.page || 1) > numberOfPages) {
        this.page = numberOfPages;
      }
    },
  },

  async created() {
    useSearchModeStore().setUsesAlgolia(this.initialSearchUsesAlgolia);

    // Children read mapIsOpen from the store on first render. Sync before they mount
    // so view=map_view opens the full-screen map without a Split placeholder flash.
    if (this.mapIsOpen) {
      useMapStore().mapIsOpen = true;
    }

    this.debouncedInlinePropertyTypeReload = debounce(() => {
      this.reloadProperties();
    }, 300);

    if (this.shortlistActive) {
      if (this.isSharedShortlist) {
        this.studentStore.hydrateSharedShortlist(this.initialProperties);
      } else {
        this.studentStore.hydrateShortlist(
          this.initialProperties,
          this.initialPropertyCount
        );
      }
    }

    if (this.enablePropertyTypeFilters) {
      const initialTypes = this.normalizePropertyTypes(this.currentType);
      const defaultTypes = this.getDefaultPropertyTypes();

      if (initialTypes.length === 0 && !this.shortlistActive) {
        this.currentType = defaultTypes;
        this.shouldReloadForDefaultPropertyTypes = true;
      }
    }

    window.addEventListener('resize', this.screenResized);

    try {
      const shortlisted = await useStudentStore().getShortlistedPropertyIds();

      this.onShortlistRestored(shortlisted.map((id) => ({ id })));
    } catch (error) {
      /* empty */
    }
  },

  destroyed() {
    window.removeEventListener('popstate', this.handleHistoryChange);
    window.removeEventListener('resize', this.screenResized);
    document.removeEventListener('click', this.handleShortlistNudgeDismissed);

    if (this.debouncedInlinePropertyTypeReload?.cancel) {
      this.debouncedInlinePropertyTypeReload.cancel();
    }

    // Clean up global sort dropdown click handler
    if (window.sortDropdownGlobalClickHandler) {
      window.removeEventListener(
        'click',
        window.sortDropdownGlobalClickHandler
      );
      delete window.sortDropdownGlobalClickHandler;
    }
  },

  async mounted() {
    const store = usePropertiesStore();

    document.addEventListener('click', this.handleShortlistNudgeDismissed);

    if (this.isLoggedInStudent) {
      this.studentStore.setStudentId(this.studentId);
      this.studentStore.setHousemateGroupLimit(this.housemateGroupCreateLimit);
      this.studentStore.setDefaultHousemateGroupId(
        this.studentDefaultHousemateGroupId
      );
      this.studentStore.handleAcceptHousemateGroupInvitation(
        this.housemateGroupInviteInitiated
      );

      if (
        !this.shortlistActive &&
        !this.enquiredProperties &&
        this.hasEnquiredProperties
      ) {
        try {
          const data = await this.studentStore.getEnquiredProperties({
            page: 1,
            bedrooms: null,
            bathrooms: null,
            minPrice: null,
            maxPrice: null,
            type: null,
            sort: this.initialCurrentSort,
            view: this.selectedView,
            lettingAgent: this.currentLandlord?.obfuscatedId,
          });

          this.prefetchedEnquiriesPage1 = data;

          if (typeof data?.propertyCount === 'number') {
            this.cachedEnquiriesPropertyCount = data.propertyCount;
          }
          if (data?.title) {
            this.cachedEnquiriesTitle = data.title;
          }
          if (data?.titleResultsCount) {
            this.cachedEnquiriesTitleResultsCount = data.titleResultsCount;
          }
        } catch {
          // ignore
        }
      }
    }

    if (this.shortlistActive) {
      this.page = 1;
      const hasShortlistFilters = Boolean(
        this.currentBedrooms ||
          this.currentBathrooms ||
          this.minPriceSelected ||
          this.maxPriceSelected ||
          this.normalizePropertyTypes(this.currentType).length ||
          this.currentLandlord
      );
      const hasCompleteAlgoliaShortlist =
        this.initialSearchUsesAlgolia &&
        !hasShortlistFilters &&
        this.initialProperties.length >= this.initialPropertyCount;

      if (this.showShortlistListsSelector) {
        this.shortlistLoading = false;
        this.shortlistResultsLoaded = true;
      } else if (!hasCompleteAlgoliaShortlist) {
        this.shortlistLoading = true;
        const shortlistSearchParams = {
          'use-search-index': 1,
          bedrooms: this.currentBedrooms,
          bathrooms: this.currentBathrooms,
          'min-price': this.currentMinPrice,
          'max-price': this.currentMaxPrice,
          type: this.currentType,
          sort: this.initialCurrentSort,
          'letting-agent': this.currentLandlord?.obfuscatedId,
        };

        try {
          if (this.isSharedShortlist) {
            await this.studentStore.getSharedShortlist(
              this.shortlistHash,
              shortlistSearchParams
            );
          } else {
            await this.studentStore.getShortlist(shortlistSearchParams, true);
          }
        } finally {
          this.shortlistLoading = false;
          this.shortlistResultsLoaded = true;
        }
      } else {
        this.shortlistLoading = false;
        this.shortlistResultsLoaded = true;
      }
    }

    const { fetchCounts } = usePropertyFilters();

    store.showIncentives =
      this.propertyIncentives ||
      document.documentElement.classList.contains('property-incentives');

    // Google Maps initialization removed for test variant

    // Sync map state with mapStore on mount if map should be open from URL
    if (this.mapIsOpen) {
      const mapStore = useMapStore();
      mapStore.mapIsOpen = this.mapIsOpen;
    }

    // Keep the address bar in sync when Grid/Split coerces the initial view
    if (
      this.canShowGridSplitMaps &&
      this.selectedView !== this.initialSelectedView
    ) {
      this.buildAndResetURL(true, false);
    }

    // The filter components read the blended decision from the store rather than a prop chain
    useSearchModeStore().setBlendedSearch(this.blendedSearch);

    // Persist the last non-enquiries search URL so we can return to it when
    // users land directly on the enquiries endpoint.
    this.persistLastNonEnquiriesSearchUrl();

    this.syncCurrentTypeFromIncludeSpareRoomsQuery();

    if (this.shouldReloadForDefaultPropertyTypes) {
      this.reloadProperties();
    }

    // Database search downloads the cached property dataset. Algolia searches
    // load aggregated facet counts from the filter modal instead.
    if (this.enableLocalFilterCounts && !this.initialSearchUsesAlgolia) {
      fetchCounts(this.currentCitySlug);
    }

    // If the user manually changes the URL with a back button press
    window.addEventListener('popstate', this.handleHistoryChange);

    // Get rid of the skeleton setup
    this.removeElementsByClass('skeleton-crew');

    // Initialize sort dropdowns
    this.reinitializeSortDropdowns();
  },

  methods: {
    handleShortlistListSelectionChange({ list, properties }) {
      this.activeShortlistList = list;
      this.activeShortlistProperties = Array.isArray(properties)
        ? properties
        : [];
      this.shortlistLoading = false;
      this.shortlistResultsLoaded = true;
      this.page = 1;
    },
    async handleShortlistPropertyRemoval(propertyId) {
      if (!propertyId || !this.activeShortlistList) return;

      const activeListHash = this.activeShortlistList.hash_id;
      this.activeShortlistProperties = this.activeShortlistProperties.filter(
        (property) => String(property?.id) !== String(propertyId)
      );

      try {
        try {
          if (activeListHash === RECENTLY_ADDED_HASH) {
            await this.studentStore.removeFromShortlist(propertyId);
          } else {
            await api.delete(
              `/account/shortlist-lists/${encodeURIComponent(
                activeListHash
              )}/properties/${encodeURIComponent(propertyId)}`
            );
          }
        } finally {
          const path =
            activeListHash === RECENTLY_ADDED_HASH
              ? '/account/shortlist-lists/recently-added/properties'
              : `/account/shortlist-lists/${encodeURIComponent(
                  activeListHash
                )}/properties`;
          const response = await api.get(path);

          if (this.activeShortlistList?.hash_id === activeListHash) {
            this.activeShortlistProperties = response.data.shortlisted || [];
          }
        }
      } catch (error) {
        const sentry = window.Sentry;
        if (sentry && typeof sentry.captureException === 'function') {
          sentry.captureException(error);
        }
      }
    },
    handleShortlistListSelectionError() {
      this.activeShortlistList = null;
      this.activeShortlistProperties = [];
      this.shortlistLoading = false;
      this.shortlistResultsLoaded = true;
    },
    onShortlistRestored(shortlist) {
      const ids = new Set(shortlist.map((property) => String(property.id)));
      for (const key of ['properties', 'nearby']) {
        this[key] = (this[key] || []).map((property) => ({
          ...property,
          shortlisted: ids.has(String(property.id)),
        }));
      }
    },
    getCurrentPath,

    // The server flag is a once-per-30-days "first eligible page view" signal, so it
    // cannot be re-read after filtering. Dismissal is tracked here instead so the
    // nudge stays hidden across tile re-renders.
    handleShortlistNudgeDismissed(event) {
      if (event.target?.closest?.('[data-action="hide-nudge"]')) {
        this.shortlistNudgeEnabled = false;
      }
    },

    persistLastNonEnquiriesSearchUrl(href = null) {
      if (this.enquiredProperties) {
        return;
      }

      try {
        const candidate = href ?? window.location.href;
        const url = new URL(candidate, window.location.origin);

        if (url.origin !== window.location.origin) {
          return;
        }

        if (!url.pathname.startsWith('/student-accommodation')) {
          return;
        }

        if (
          url.searchParams.get('enquired-properties') === '1' ||
          url.searchParams.get('enquired_properties') === '1'
        ) {
          return;
        }

        // Avoid persisting the base route as it can lead to a broken state.
        if (
          url.pathname === '/student-accommodation' ||
          url.pathname === '/student-accommodation/'
        ) {
          return;
        }

        sessionStorage.setItem(
          'uh_student_accommodation_last_search_url',
          url.href
        );
      } catch {
        // ignore
      }
    },

    getLastNonEnquiriesSearchUrl() {
      const candidates = [];

      if (this.lastSearchUrl) {
        candidates.push(this.lastSearchUrl);
      }

      try {
        const stored = sessionStorage.getItem(
          'uh_student_accommodation_last_search_url'
        );
        if (stored) {
          candidates.push(stored);
        }
      } catch {
        // ignore
      }

      if (document.referrer) {
        candidates.push(document.referrer);
      }

      for (const candidate of candidates) {
        try {
          const url = new URL(candidate, window.location.origin);

          if (url.origin !== window.location.origin) {
            continue;
          }

          if (!url.pathname.startsWith('/student-accommodation')) {
            continue;
          }

          if (
            url.searchParams.get('enquired-properties') === '1' ||
            url.searchParams.get('enquired_properties') === '1'
          ) {
            continue;
          }

          if (
            url.pathname === '/student-accommodation' ||
            url.pathname === '/student-accommodation/'
          ) {
            continue;
          }

          return url.href;
        } catch {
          // ignore
        }
      }

      return null;
    },

    resetSearchStateForEnquiries() {
      // Enquired properties is an override view; clear other filters so the UI
      // matches the overridden results.
      this.currentAreaSlug = null;
      this.currentUniversitySlug = null;
      this.currentCitySlug = null;
      this.currentBedrooms = null;
      this.currentBathrooms = null;
      this.currentMinPrice = null;
      this.currentMaxPrice = null;
      this.minPriceSelected = false;
      this.maxPriceSelected = false;
      this.currentType = null;
      this.currentSort = this.initialCurrentSort;
      this.page = 1;
    },

    // Google Maps related methods removed for test variant
    // Google Maps related methods removed for test variant

    bathroomCountCannotExceedBedroomCount() {
      if (!this.currentBedrooms) {
        return;
      }

      if (
        !this.useExactRoomCounts &&
        this.currentBathrooms > this.currentBedrooms
      ) {
        this.currentBathrooms = this.currentBedrooms;
      }
    },

    buildAndResetURL(replaceState = false, reload = true) {
      /* Build the url */
      let url =
          '/student-accommodation' +
          (!this.enquiredProperties && this.currentCitySlug
            ? '/' + this.currentCitySlug
            : ''),
        qs = {};

      /* Add in parameters and querystring */
      // Enquired properties is a search override for *location* (city/area/university)
      // but we still allow filtering within enquiries (beds/baths/price/type/etc).
      if (!this.enquiredProperties) {
        if (this.currentAreaSlug) {
          url += '/' + this.currentAreaSlug;

          if (this.currentUniversitySlug) {
            qs.university = this.currentUniversitySlug;
          }
        } else if (this.currentUniversitySlug) {
          url += '/near-' + this.currentUniversitySlug;
        }
      }

      if (this.currentBedrooms) {
        qs['bedrooms'] = this.currentBedrooms;
      }
      if (this.currentBathrooms) {
        qs['bathrooms'] = this.currentBathrooms;
      }
      if (this.currentMinPrice && this.minPriceSelected) {
        qs['min-price'] = this.currentMinPrice;
      }
      if (this.currentMaxPrice && this.maxPriceSelected) {
        qs['max-price'] = this.currentMaxPrice;
      }
      this.applyTypeAndSpareRoomsToQuery(qs);
      if (this.currentLandlord) {
        qs['letting-agent'] = this.currentLandlord.obfuscatedId;
      }
      if (this.selectedView && this.selectedView !== 'list_view') {
        qs['view'] = this.selectedView;
      }
      if (
        this.currentSort !== '' &&
        this.currentSort !== 'relevance' &&
        this.currentSort !== undefined
      ) {
        qs['sort'] = this.currentSort;
      }
      if (this.page && this.page > 1) {
        qs['page'] = this.page;
      }
      if (this.enquiredProperties) {
        qs['enquired-properties'] = 1;
      }

      /* Reset the URL */
      let push =
        url +
        (this.objectIsEmpty(qs) === false
          ? '?' + this.objectToQueryString(qs)
          : '');

      const shouldPreserveUrl =
        this.enquiredProperties && this.preserveSearchUrlDuringEnquiriesTab;

      if (!shouldPreserveUrl) {
        if (!this.enquiredProperties) {
          this.persistLastNonEnquiriesSearchUrl(push);
        }

        if (replaceState) {
          window.history.replaceState(null, this.propertiesSearchTitle, push);
        } else if (reload) {
          window.history.pushState(null, this.propertiesSearchTitle, push);
        }
      }

      if (reload) {
        qs['reload'] = 'true';
      }
      return url + '?' + this.objectToQueryString(qs);
    },
    // Google Maps related methods removed for test variant

    async fetchEnquiries(loadType) {
      const scope = this;

      // Keep the last known title/count visible while loading to avoid UI
      // flicker when toggling between tabs.
      if (!this.title && this.cachedEnquiriesTitle) {
        this.title = this.cachedEnquiriesTitle;
      }

      if (!this.titleResultsCount && this.cachedEnquiriesTitleResultsCount) {
        this.titleResultsCount = this.cachedEnquiriesTitleResultsCount;
      }

      try {
        const data = await this.studentStore.getEnquiredProperties({
          page: scope.page,
          bedrooms: scope.currentBedrooms,
          bathrooms: scope.currentBathrooms,
          minPrice: scope.minPriceSelected ? scope.currentMinPrice : null,
          maxPrice: scope.maxPriceSelected ? scope.currentMaxPrice : null,
          type: scope.currentType,
          sort: scope.currentSort,
          view: scope.selectedView,
          lettingAgent: scope.currentLandlord?.obfuscatedId,
        });

        const properties = Array.isArray(data.properties)
          ? data.properties
          : [];

        scope.hideNearby = data.hideNearby;
        scope.maxCheapestRoomPrice = parseInt(data.maxCheapestRoomPrice);

        // If the max price wasn't selected by the user, reset the currentMaxPrice to the returned max price
        if (!scope.maxPriceSelected) {
          scope.currentMaxPrice = scope.maxCheapestRoomPrice;
        }

        scope.nearby = data.nearby;
        scope.properties =
          loadType === 'loading'
            ? scope.properties.concat(properties)
            : properties;

        scope.propertiesHasMorePages = data.propertiesHasMorePages;
        scope.propertyCount = data.propertyCount;
        scope.cachedEnquiriesPropertyCount = data.propertyCount;
        scope.resultsCountLoading = false;
        if (
          typeof data.showNearby === 'number' &&
          !Number.isNaN(data.showNearby)
        ) {
          scope.showNearbyValue = data.showNearby;
        }
        scope.title = data.title;
        scope.titleResultsCount = data.titleResultsCount;

        if (data.title) {
          scope.cachedEnquiriesTitle = data.title;
        }
        if (data.titleResultsCount) {
          scope.cachedEnquiriesTitleResultsCount = data.titleResultsCount;
        }

        // Match legacy analytics behaviour from fetchProperties().
        try {
          if (scope.analytics === 'gtag') {
            gtag('set', 'page_path', scope.buildAndResetURL(false, false));
            gtag('event', 'page_view');
          } else {
            ga('create', 'UA-67694394-1', 'auto');
            ga('send', 'pageview', scope.buildAndResetURL(false, false));
          }
        } catch {
          // ignore
        }
      } catch {
        // Keep a consistent enquiries override view rather than
        // falling back to stale search results.
        scope.properties = [];
        scope.propertyCount = 0;
        scope.cachedEnquiriesPropertyCount = 0;
        scope.resultsCountLoading = false;
        scope.nearby = [];
        scope.hideNearby = true;
      } finally {
        if (loadType === 'loading') {
          scope.loading = false;
        } else {
          scope.reloading = false;
        }

        scope.resultsCountLoading = false;
        // Reinitialize sort dropdowns after content update
        scope.reinitializeSortDropdowns();
      }
    },

    fetchProperties(loadType) {
      if (this.enquiredProperties) {
        // Enquiries mode uses the Pinia store + API call.
        this.fetchEnquiries(loadType);
        return;
      }

      let scope = this,
        get = this.buildAndResetURL();

      // Grid/Split keep view=grid_view|split_view so page-size (+1 for grid) and
      // the address bar stay correct, but listing fetches must opt into the full
      // results payload — without results=1 the server returns MapDataResource
      // (pins only; no nearby/title), which breaks card pagination.
      if (this.isGridOrSplitSelectedView) {
        try {
          const listingUrl = new URL(get, window.location.origin);
          listingUrl.searchParams.set('results', '1');
          get = listingUrl.pathname + listingUrl.search;
        } catch {
          // Keep the original URL if parsing fails.
        }
      }

      // Keep the last known title/count visible while loading to avoid UI
      // flicker when returning from Enquiries to Search.
      if (!this.title && this.cachedSearchTitle) {
        this.title = this.cachedSearchTitle;
      }

      if (!this.titleResultsCount && this.cachedSearchTitleResultsCount) {
        this.titleResultsCount = this.cachedSearchTitleResultsCount;
      }

      axios
        .get(get)
        .then(function (response) {
          scope.hideNearby = response.data.hideNearby;
          scope.maxCheapestRoomPrice = parseInt(
            response.data.maxCheapestRoomPrice
          );

          // If the max price wasn't selected by the user, reset the currentMaxPrice to the returned max price
          if (!scope.maxPriceSelected) {
            scope.currentMaxPrice = scope.maxCheapestRoomPrice;
          }

          const nextProperties = Array.isArray(response.data.properties)
            ? response.data.properties
            : [];
          const currentProperties = Array.isArray(scope.properties)
            ? scope.properties
            : [];

          scope.nearby = Array.isArray(response.data.nearby)
            ? response.data.nearby
            : [];
          scope.properties =
            loadType === 'loading'
              ? currentProperties.concat(nextProperties)
              : nextProperties;

          scope.propertiesHasMorePages = response.data.propertiesHasMorePages;
          scope.propertyCount = response.data.propertyCount;
          if (scope.enquiredProperties) {
            scope.cachedEnquiriesPropertyCount = response.data.propertyCount;
          } else {
            scope.cachedSearchPropertyCount = response.data.propertyCount;
          }
          scope.resultsCountLoading = false;
          if (
            typeof response.data.showNearby === 'number' &&
            !Number.isNaN(response.data.showNearby)
          ) {
            scope.showNearbyValue = response.data.showNearby;
          }
          scope.title = response.data.title;
          scope.titleResultsCount = response.data.titleResultsCount;

          if (
            Object.prototype.hasOwnProperty.call(
              response.data,
              'showSpareRoomsOneBedNudge'
            )
          ) {
            scope.showSpareRoomsOneBedNudge =
              !!response.data.showSpareRoomsOneBedNudge;
          }
          if (
            Object.prototype.hasOwnProperty.call(response.data, 'blendedSearch')
          ) {
            scope.blendedSearch = !!response.data.blendedSearch;
            useSearchModeStore().setBlendedSearch(scope.blendedSearch);
          }
          if (
            Object.prototype.hasOwnProperty.call(
              response.data,
              'searchUsesAlgolia'
            )
          ) {
            useSearchModeStore().setUsesAlgolia(
              !!response.data.searchUsesAlgolia
            );
          }
          if (
            Object.prototype.hasOwnProperty.call(
              response.data,
              'spareRoomsOneBedOverlayCount'
            )
          ) {
            scope.spareRoomsOneBedOverlayCount = parseInt(
              response.data.spareRoomsOneBedOverlayCount,
              10
            );
            if (Number.isNaN(scope.spareRoomsOneBedOverlayCount)) {
              scope.spareRoomsOneBedOverlayCount = 0;
            }
          }
          if (
            Object.prototype.hasOwnProperty.call(
              response.data,
              'spareRoomsOneBedOverlayApplyUrl'
            )
          ) {
            let applyU = response.data.spareRoomsOneBedOverlayApplyUrl || '';
            if (applyU) {
              try {
                const u = new URL(applyU, window.location.href);
                u.searchParams.delete('reload');
                applyU = u.pathname + u.search + u.hash;
              } catch {
                // keep string as returned if URL parsing fails
              }
            }
            scope.spareRoomsOneBedOverlayApplyUrl = applyU;
          }
          if (response.data.title) {
            scope.cachedSearchTitle = response.data.title;
          }
          if (response.data.titleResultsCount) {
            scope.cachedSearchTitleResultsCount =
              response.data.titleResultsCount;
          }
          // Google Maps rebuild removed for test variant
          // Send the call to GA
          if (scope.analytics === 'gtag') {
            gtag('set', 'page_path', scope.buildAndResetURL(false, false));
            gtag('event', 'page_view');
          } else {
            ga('create', 'UA-67694394-1', 'auto');
            ga('send', 'pageview', scope.buildAndResetURL(false, false));
          }
        })
        .catch(function () {
          // TODO: Handle any errors
        })
        .then(function () {
          if (loadType === 'loading') {
            scope.loading = false;
          } else {
            scope.reloading = false;
          }

          scope.resultsCountLoading = false;
          // Reinitialize sort dropdowns after content update
          scope.reinitializeSortDropdowns();
        });
    },

    getParameterByName(name, url) {
      name = name.replace(/[[\]]/g, '\\$&');
      var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
        results = regex.exec(url);
      if (!results) return null;
      if (!results[2]) return '';
      return decodeURIComponent(results[2].replace(/\+/g, ' '));
    },

    // Google Maps related methods removed for test variant

    handleHistoryChange() {
      var history = this.history.pop();

      if (history) {
        // If the current view doesn't match the history view, sync view state
        if (this.selectedView !== history.view) {
          if (this.canShowGridSplitMaps) {
            this.setSelectedViewAndSync(history.view, false);
          } else {
            this.toggleMapIsOpen(false);
          }
          return;
        }

        /**
         * When we come back from an individual property page on iOS, the popstate gets fired
         * This creates a reload action when we don't want one
         * Check to make sure that the history location and the current location match up
         * This means that the switch back through the history matches correctly
         */
        if (window.location.href !== history.location) {
          return undefined;
        }

        this.currentAreaSlug = history.areaSlug;
        this.currentBathrooms = history.bathrooms;
        this.currentBedrooms = history.bedrooms;
        this.currentCitySlug = history.citySlug;
        this.currentMaxPrice = history.maxPrice;
        this.page = history.page;
        this.currentSort = history.sort;
        this.currentType = history.type;
        this.currentUniversitySlug = history.universitySlug;
        this.enquiredProperties = history.enquiredProperties;

        // Set view to reloading status
        this.reloading = true;

        this.fetchProperties('reloading');
      }
    },
    handleScroll() {
      this.currentScroll = window.scrollY;
    },
    // Google Maps related methods removed for test variant
    makeId(length) {
      let result = '',
        characters =
          'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789',
        charactersLength = characters.length;
      for (let i = 0; i < length; i++) {
        result += characters.charAt(
          Math.floor(Math.random() * charactersLength)
        );
      }
      return result;
    },
    objectIsEmpty(obj) {
      for (let prop in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, prop)) {
          return false;
        }
      }

      return JSON.stringify(obj) === JSON.stringify({});
    },
    objectToQueryString(obj) {
      const str = [];
      for (const p of Object.keys(obj)) {
        if (!Object.prototype.hasOwnProperty.call(obj, p)) {
          continue;
        }
        const v = obj[p];
        if (v === undefined || v === null) {
          continue;
        }
        if (Array.isArray(v)) {
          if (p === 'type') {
            const joined = v
              .filter(
                (item) => item !== undefined && item !== null && item !== ''
              )
              .map((item) => String(item).trim())
              .filter(Boolean)
              .join(',');
            if (joined !== '') {
              str.push(
                encodeURIComponent(p) + '=' + encodeURIComponent(joined)
              );
            }
            continue;
          }
          for (const item of v) {
            if (item === undefined || item === null) {
              continue;
            }
            str.push(
              encodeURIComponent(`${p}[]`) +
                '=' +
                encodeURIComponent(String(item))
            );
          }
        } else {
          str.push(encodeURIComponent(p) + '=' + encodeURIComponent(String(v)));
        }
      }
      return str.join('&');
    },
    normalizePropertyTypes(value) {
      if (value === null || value === undefined || value === '') {
        return [];
      }
      if (Array.isArray(value)) {
        return [
          ...new Set(value.filter(Boolean).map((t) => String(t).trim())),
        ].filter(Boolean);
      }
      if (typeof value === 'string') {
        return value
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      }
      return [];
    },
    getDefaultPropertyTypes() {
      return getDefaultPropertyTypeFilters(
        this.enablePrivateHallsFilter,
        this.blendedSearch && this.enableSpareRoomFunctionality
      );
    },
    /**
     * Matches {@see PropertyService::isSpareRoomsExclusiveListing}: spare-rooms token
     * present with no structural home-type tokens.
     */
    isExclusiveSpareRoomsListingType(value) {
      const parts = this.normalizePropertyTypes(value);
      if (!parts.includes('spare-rooms')) {
        return false;
      }
      const structural = parts.filter(
        (t) => t === 'house' || t === 'apartment' || t === 'private-halls'
      );
      return structural.length === 0;
    },
    applyTypeAndSpareRoomsToQuery(qs) {
      if (!this.currentType) {
        return;
      }
      const parts = this.normalizePropertyTypes(this.currentType);
      if (parts.length === 0) {
        return;
      }

      // Comma-separated `type=` (same as e.g. type=private-halls,house).
      qs['type'] = parts.join(',');
    },
    syncCurrentTypeFromIncludeSpareRoomsQuery() {
      if (!this.enableSpareRoomFunctionality) {
        return;
      }
      try {
        const params = new URLSearchParams(window.location.search);
        if (params.get('include-spare-rooms') !== '1') {
          return;
        }
        if (String(this.currentBedrooms) !== '1') {
          return;
        }
        const parts = this.normalizePropertyTypes(this.currentType);
        if (parts.includes('spare-rooms')) {
          return;
        }
        const structural = parts.filter(
          (t) => t === 'house' || t === 'apartment'
        );
        if (structural.length === 0) {
          return;
        }
        this.currentType = [...structural, 'spare-rooms'];
      } catch {
        // ignore
      }
    },
    pushToHistory() {
      this.history.push({
        areaSlug: this.currentAreaSlug,
        bathrooms: this.currentBathrooms,
        bedrooms: this.currentBedrooms,
        citySlug: this.currentCitySlug,
        location: window.location.href,
        minPrice: this.currentMinPrice,
        maxPrice: this.currentMaxPrice,
        page: this.page,
        selectedView: this.selectedView,
        sort: this.currentSort,
        type: this.currentType,
        universitySlug: this.currentUniversitySlug,
        view: this.selectedView,
        enquiredProperties: this.enquiredProperties,
      });
    },
    returnFilterBarTitle(modelClass, model, excludeFormatting = false) {
      if (modelClass === 'City') {
        return excludeFormatting
          ? `Student Accommodation in ${model.name}`
          : `<span class="d-block font-size-10px font-weight-normal">Student Accommodation in</span>
                    <span class="d-block font-size-22px font-weight-700 ls-0-2px pb-5px">${model.name}</span>`;
      }

      if (modelClass === 'Area') {
        return excludeFormatting
          ? `Student Accommodation in ${model.name}` +
              (model.city && model.city.name ? `, ${model.city.name}` : '')
          : `<span class="d-block font-size-10px font-weight-normal">Student Accommodation in</span>
                    <span class="d-block font-size-22px font-weight-700 ls-0-2px pb-5px">${model.name}` +
              (model.city && model.city.name ? `, ${model.city.name}` : '') +
              `</span>`;
      }
      if (modelClass === 'University') {
        return excludeFormatting
          ? `Student Accommodation near ${model.name}`
          : `<span class="d-block font-size-10px font-weight-normal">Student Accommodation near</span>
                    <span class="d-block font-size-22px font-weight-700 ls-0-2px pb-5px mr-10px py-0">${model.name}</span>`;
      }

      return null;
    },
    screenResized() {
      let scope = this;
      this.$nextTick(function () {
        scope.screenKey = scope.makeId(5);
        scope.windowHeight = window.innerHeight;
        scope.windowWidth = window.innerWidth;
      });
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    setCurrentSort(sort) {
      this.pushToHistory();
      this.currentSort = sort;
      this.reloadProperties();
    },
    // Google Maps related methods removed for test variant
    showMoreProperties() {
      if (this.shortlistActive) return;

      this.loading = true;
      this.page = this.page ? ++this.page : 2;

      this.fetchProperties('loading');
    },
    reloadProperties(page = 1) {
      if (this.shortlistActive) {
        this.page = page;
        this.reloading = false;
        this.scrollToTop();
        return;
      }

      this.reloading = true;
      // We want to move to the start of the filtered results but not on pagination
      this.page = page;
      this.scrollToTop();

      this.fetchProperties('reloading');
    },

    removeElementsByClass(className) {
      const elements = document.getElementsByClassName(className);
      while (elements.length > 0) {
        elements[0].parentNode.removeChild(elements[0]);
      }
    },
    // Google Maps related methods removed for test variant
    setCurrentAreaSlug(slug) {
      if (this.shortlistActive) return;

      this.pushToHistory();
      this.currentAreaSlug = slug;
    },
    setCurrentBathrooms(rooms) {
      this.pushToHistory();
      this.currentBathrooms = rooms;
      this.bathroomCountCannotExceedBedroomCount();
    },
    setCurrentBedrooms(rooms) {
      this.pushToHistory();
      this.currentBedrooms = rooms;
      this.bathroomCountCannotExceedBedroomCount();
    },
    setCurrentCitySlug(slug) {
      if (this.shortlistActive) return;

      this.currentCitySlug = slug;
      if (!this.currentCity) {
        // Redirect to /student-accommodation
        window.location.href = '/student-accommodation/';
        return;
      }
      window.location.href = this.currentCity.path;
    },
    setCurrentMinPrice(price) {
      this.currentMinPrice = price;
    },
    setCurrentMaxPrice(price) {
      this.currentMaxPrice = price;
    },
    setCurrentType(type) {
      this.pushToHistory();

      const wasExclusiveSpareRooms = this.isExclusiveSpareRoomsListingType(
        this.currentType
      );
      const normalizedParts = this.normalizePropertyTypes(type);

      if (normalizedParts.length === 0) {
        this.currentType = this.getDefaultPropertyTypes().join(',');
        return;
      }

      // Blended results answer the bedroom filter for spare rooms too, so selecting them is an
      // ordinary type choice: the selection is kept whole and the bedroom count survives.
      const incomingExclusiveSpareRooms =
        !this.blendedSearch &&
        this.isExclusiveSpareRoomsListingType(normalizedParts);

      if (normalizedParts.length === 0) {
        this.currentType = null;
      } else if (incomingExclusiveSpareRooms) {
        this.currentType = 'spare-rooms';
      } else {
        this.currentType = normalizedParts.join(',');
      }

      // Only strip bedrooms when the user moves into spare-rooms-only (not when switching to other types).
      if (incomingExclusiveSpareRooms && !wasExclusiveSpareRooms) {
        this.currentBedrooms = null;
      }
    },
    setCurrentUniversitySlug(slug) {
      if (this.shortlistActive) return;

      this.pushToHistory();
      this.currentUniversitySlug = slug;
      this.reloadProperties();
    },
    setMinPriceSelected(boolean) {
      this.pushToHistory();
      this.minPriceSelected = boolean;
    },
    setMaxPriceSelected(boolean) {
      this.pushToHistory();
      this.maxPriceSelected = boolean;
    },
    setEnquiredProperties(checked) {
      this.pushToHistory();
      this.enquiredProperties = checked;
    },
    async setEnquiredPropertiesFromTab(checked) {
      if (checked && !this.isLoggedInStudent) {
        return;
      }

      if (checked === this.enquiredProperties) {
        return;
      }

      if (checked) {
        this.preserveSearchUrlDuringEnquiriesTab = true;

        // Cache the current search title/count so switching back doesn't
        // cause the header to disappear while results reload.
        if (this.title) {
          this.cachedSearchTitle = this.title;
        }
        if (this.titleResultsCount) {
          this.cachedSearchTitleResultsCount = this.titleResultsCount;
        }

        this.enquiriesReturnState = {
          currentAreaSlug: this.currentAreaSlug,
          currentUniversitySlug: this.currentUniversitySlug,
          currentCitySlug: this.currentCitySlug,
          currentBedrooms: this.currentBedrooms,
          currentBathrooms: this.currentBathrooms,
          currentMinPrice: this.currentMinPrice,
          currentMaxPrice: this.currentMaxPrice,
          minPriceSelected: this.minPriceSelected,
          maxPriceSelected: this.maxPriceSelected,
          currentType: this.currentType,
          currentSort: this.currentSort,
          page: this.page,
          selectedView: this.selectedView,
          title: this.title,
          titleResultsCount: this.titleResultsCount,
          properties: Array.isArray(this.properties)
            ? this.properties.slice()
            : [],
          nearby: Array.isArray(this.nearby) ? this.nearby.slice() : [],
          hideNearby: this.hideNearby,
          propertiesHasMorePages: this.propertiesHasMorePages,
          propertyCount: this.propertyCount,
          maxCheapestRoomPrice: this.maxCheapestRoomPrice,
          showNearbyValue: this.showNearbyValue,
        };

        this.resetSearchStateForEnquiries();

        const prefetched = this.prefetchedEnquiriesPage1;
        const canUsePrefetchedEnquiries =
          prefetched &&
          this.page === 1 &&
          !this.currentBedrooms &&
          !this.currentBathrooms &&
          !this.currentMinPrice &&
          !this.currentMaxPrice &&
          !this.currentType &&
          this.currentSort === this.initialCurrentSort;

        // Seed the results count immediately using a cached enquiries count
        // where possible. This avoids showing the previous (search) count
        // under the Enquiries label.
        let cachedEnquiriesCount = null;

        if (
          typeof this.cachedEnquiriesPropertyCount === 'number' &&
          !Number.isNaN(this.cachedEnquiriesPropertyCount)
        ) {
          cachedEnquiriesCount = this.cachedEnquiriesPropertyCount;
        } else {
          cachedEnquiriesCount = this.studentStore?.enquiriesPropertyCount;
        }

        if (cachedEnquiriesCount !== null) {
          this.propertyCount = cachedEnquiriesCount;
          this.resultsCountLoading = false;
        } else {
          this.propertyCount = 0;
          this.resultsCountLoading = true;
        }

        this.hideNearby = true;
        this.page = 1;
        this.scrollToTop();

        this.enquiredProperties = true;

        if (!this.hasEnquiredProperties) {
          this.properties = [];
          this.propertyCount = 0;
          this.cachedEnquiriesPropertyCount = 0;
          this.resultsCountLoading = false;
          this.nearby = [];
          this.hideNearby = true;
          this.reloading = false;
          this.reinitializeSortDropdowns();
          return;
        }

        if (canUsePrefetchedEnquiries) {
          const properties = Array.isArray(prefetched.properties)
            ? prefetched.properties
            : [];

          this.hideNearby = prefetched.hideNearby;
          this.maxCheapestRoomPrice = parseInt(prefetched.maxCheapestRoomPrice);
          if (!this.maxPriceSelected) {
            this.currentMaxPrice = this.maxCheapestRoomPrice;
          }

          this.nearby = prefetched.nearby;
          this.properties = properties;
          this.propertiesHasMorePages = prefetched.propertiesHasMorePages;
          this.propertyCount = prefetched.propertyCount;
          this.cachedEnquiriesPropertyCount = prefetched.propertyCount;
          this.resultsCountLoading = false;
          if (
            typeof prefetched.showNearby === 'number' &&
            !Number.isNaN(prefetched.showNearby)
          ) {
            this.showNearbyValue = prefetched.showNearby;
          }
          this.title = prefetched.title;
          this.titleResultsCount = prefetched.titleResultsCount;

          if (prefetched.title) {
            this.cachedEnquiriesTitle = prefetched.title;
          }
          if (prefetched.titleResultsCount) {
            this.cachedEnquiriesTitleResultsCount =
              prefetched.titleResultsCount;
          }

          this.reloading = false;
          this.loading = false;
          this.reinitializeSortDropdowns();
          return;
        }

        this.reloading = true;

        await this.fetchEnquiries('reloading');

        return;
      }

      // If we landed directly on enquiries (no return state), redirect back to
      // the last known search URL (city/area/university) instead of returning
      // to the base /student-accommodation route.
      if (!this.enquiriesReturnState) {
        const returnUrl = this.getLastNonEnquiriesSearchUrl();
        if (returnUrl) {
          window.location.href = returnUrl;
          return;
        }
      }

      const returnState = this.enquiriesReturnState;
      if (returnState) {
        this.currentAreaSlug = returnState.currentAreaSlug;
        this.currentUniversitySlug = returnState.currentUniversitySlug;
        this.currentCitySlug = returnState.currentCitySlug;
        this.currentBedrooms = returnState.currentBedrooms;
        this.currentBathrooms = returnState.currentBathrooms;
        this.currentMinPrice = returnState.currentMinPrice;
        this.currentMaxPrice = returnState.currentMaxPrice;
        this.minPriceSelected = returnState.minPriceSelected;
        this.maxPriceSelected = returnState.maxPriceSelected;
        this.currentType = returnState.currentType;
        this.currentSort = returnState.currentSort;
        this.page = returnState.page;
        this.selectedView = returnState.selectedView;

        if (returnState.title) {
          this.title = returnState.title;
          this.cachedSearchTitle = returnState.title;
        } else if (this.cachedSearchTitle) {
          this.title = this.cachedSearchTitle;
        }

        if (returnState.titleResultsCount) {
          this.titleResultsCount = returnState.titleResultsCount;
          this.cachedSearchTitleResultsCount = returnState.titleResultsCount;
        } else if (this.cachedSearchTitleResultsCount) {
          this.titleResultsCount = this.cachedSearchTitleResultsCount;
        }
      }

      this.enquiredProperties = false;
      this.enquiriesReturnState = null;
      const wasPreservingUrl = this.preserveSearchUrlDuringEnquiriesTab;
      this.preserveSearchUrlDuringEnquiriesTab = false;

      if (!wasPreservingUrl) {
        this.buildAndResetURL(true, false);
      }

      if (
        typeof this.cachedSearchPropertyCount === 'number' &&
        !Number.isNaN(this.cachedSearchPropertyCount)
      ) {
        this.propertyCount = this.cachedSearchPropertyCount;
        this.resultsCountLoading = false;
      } else {
        this.resultsCountLoading = true;
      }

      if (returnState && Array.isArray(returnState.properties)) {
        this.properties = returnState.properties;
        this.nearby = Array.isArray(returnState.nearby)
          ? returnState.nearby
          : [];

        if (typeof returnState.hideNearby === 'boolean') {
          this.hideNearby = returnState.hideNearby;
        }
        if (typeof returnState.propertiesHasMorePages === 'boolean') {
          this.propertiesHasMorePages = returnState.propertiesHasMorePages;
        }
        if (
          typeof returnState.propertyCount === 'number' &&
          !Number.isNaN(returnState.propertyCount)
        ) {
          this.propertyCount = returnState.propertyCount;
          this.cachedSearchPropertyCount = returnState.propertyCount;
          this.resultsCountLoading = false;
        }
        if (
          typeof returnState.maxCheapestRoomPrice === 'number' &&
          !Number.isNaN(returnState.maxCheapestRoomPrice)
        ) {
          this.maxCheapestRoomPrice = returnState.maxCheapestRoomPrice;
        }
        if (
          typeof returnState.showNearbyValue === 'number' &&
          !Number.isNaN(returnState.showNearbyValue)
        ) {
          this.showNearbyValue = returnState.showNearbyValue;
        }

        this.reloading = false;
        this.loading = false;
        this.scrollToTop();
        this.reinitializeSortDropdowns();
        return;
      }

      this.reloadProperties(this.page || 1);
    },
    canRedirect(targetHref) {
      try {
        const currentUrl = new URL(window.location.href);
        const targetUrl = new URL(targetHref, currentUrl.origin);

        if (!this.redirectEnquiredProperties) {
          return false;
        }

        return (
          currentUrl.pathname + currentUrl.search !==
          targetUrl.pathname + targetUrl.search
        );
      } catch {
        return false;
      }
    },
    getRedirectUrl() {
      const lastSearchUrl = this.lastSearchUrl;
      const currentPath = this.getCurrentPath();
      const enquiredProperties = this.enquiredProperties;

      if (
        lastSearchUrl &&
        !enquiredProperties &&
        currentPath &&
        currentPath === '/student-accommodation'
      ) {
        return lastSearchUrl;
      }

      if (enquiredProperties) {
        if (this.preserveSearchUrlDuringEnquiriesTab) {
          return;
        }

        const hasEnquiriesFilters =
          !!this.currentBedrooms ||
          !!this.currentBathrooms ||
          (this.currentMinPrice && this.minPriceSelected) ||
          (this.currentMaxPrice && this.maxPriceSelected) ||
          !!this.currentType ||
          !!this.currentLandlord ||
          (this.page && this.page > 1) ||
          (this.selectedView && this.selectedView !== 'list_view') ||
          (this.currentSort &&
            this.currentSort !== this.initialCurrentSort &&
            this.currentSort !== 'relevance');

        if (!hasEnquiriesFilters) {
          return ENQUIRED_PROPERTIES_URL;
        }
      }
    },
    handleModalClose() {
      const redirectUrl = this.getRedirectUrl();

      if (
        redirectUrl === ENQUIRED_PROPERTIES_URL &&
        this.canRedirect(redirectUrl)
      ) {
        window.history.replaceState(
          null,
          this.propertiesSearchTitle,
          redirectUrl
        );
      }

      this.reloadProperties();
    },
    handleDesktopPropertyFiltersOpen() {
      this.desktopPropertyFiltersOpen = true;
    },
    handleDesktopPropertyFiltersClose() {
      this.desktopPropertyFiltersOpen = false;
    },
    handleInlinePropertyTypeFilter(propertyType) {
      if (propertyType !== undefined) {
        this.setCurrentType(propertyType);
      }
      this.debouncedInlinePropertyTypeReload();
    },
    setPage(page) {
      this.pushToHistory();
      this.page = page;
      this.reloadProperties(page);
      this.scrollToTop();
    },
    setPageNumber(iteration) {
      this.page =
        Math.floor(iteration / this.paginate) > 0
          ? Math.ceil(iteration / this.paginate)
          : 1;
      this.buildAndResetURL(true);
    },
    setSelectedView(view) {
      this.selectedView = resolveMarketplaceSelectedView(
        view,
        this.canShowGridSplitMaps
      );
    },
    setSelectedViewAndSync(view, pushToHistory = true) {
      // Snapshot the current view/URL before switching (same order as filter setters)
      // so browser back and "Back to results" can restore Grid/Split.
      if (pushToHistory !== false) {
        this.pushToHistory();
      }

      const previousView = this.selectedView;
      this.setSelectedView(view);

      // Remember Grid/Split so "Back to results" can restore it after fullscreen.
      if (
        this.selectedView === 'map_view' &&
        (previousView === 'grid_view' || previousView === 'split_view')
      ) {
        this.marketplaceViewBeforeFullscreen = previousView;
      } else if (this.selectedView !== 'map_view') {
        this.marketplaceViewBeforeFullscreen = null;
      }

      const mapStore = useMapStore();
      const isFullMap = this.selectedView === 'map_view';
      this.mapIsOpen = isFullMap;
      mapStore.mapIsOpen = isFullMap;

      if (pushToHistory !== false) {
        this.buildAndResetURL(false, true);
      }
    },
    // Google Maps related methods removed for test variant
    toggleMapIsOpen(pushToHistory = true) {
      // Desktop Grid/Split: closing the full-page map returns to the prior
      // Grid/Split mode. Opening still uses setSelectedViewAndSync('map_view').
      // Mobile keeps the legacy fullscreen map toggle.
      if (this.canShowGridSplitMaps && window.innerWidth >= 768) {
        if (this.selectedView === 'map_view') {
          const restoreView =
            this.marketplaceViewBeforeFullscreen || 'split_view';
          this.setSelectedViewAndSync(restoreView, pushToHistory);
          return;
        }

        this.setSelectedViewAndSync(
          this.selectedView === 'grid_view' ? 'grid_view' : 'split_view',
          pushToHistory
        );
        return;
      }

      this.mapIsOpen = !this.mapIsOpen;
      // Sync with mapStore for child components
      const mapStore = useMapStore();
      mapStore.mapIsOpen = this.mapIsOpen;
      if (pushToHistory !== false) {
        this.pushToHistory();
      }
      this.setSelectedView(this.mapIsOpen ? 'map_view' : 'list_view');
      this.buildAndResetURL(false, true);
    },
    reinitializeSortDropdowns() {
      this.$nextTick(() => {
        // Only initialize the global click handler once
        if (!window.sortDropdownGlobalClickHandler) {
          const handleGlobalClick = (e) => {
            // Close any open dropdowns when clicking outside them
            document
              .querySelectorAll('.custom-select.open')
              .forEach((select) => {
                if (!select.contains(e.target)) {
                  select.classList.remove('open');
                }
              });
          };

          window.sortDropdownGlobalClickHandler = handleGlobalClick;
          window.addEventListener('click', handleGlobalClick);
        }

        // Add individual click handlers to dropdown wrappers
        document
          .querySelectorAll(
            '.custom-select-wrapper:not([data-dropdown-initialized]), .custom-select-wrapper-bw:not([data-dropdown-initialized])'
          )
          .forEach((wrapper) => {
            wrapper.setAttribute('data-dropdown-initialized', 'true');

            wrapper.addEventListener('click', function (e) {
              const select = this.querySelector('.custom-select');

              // If clicking on an option, close the dropdown and let Vue handle it
              if (e.target.closest('.custom-option')) {
                select.classList.remove('open');
                return;
              }

              // Otherwise, toggle the dropdown
              select.classList.toggle('open');
            });
          });
      });
    },
    // Google Maps related methods removed for test variant
  },
};
</script>

<style>
.gm-style-iw-chr {
  display: none;
  /* <-- this will generally work on the fly. */
  visibility: hidden;
  /* this 2 lines below are just for hard hiding. :) */
  opacity: 0;
}

.gm-style-iw-d {
  overflow: hidden !important;
}

.gm-style .gm-style-iw-c {
  padding: 0;
  border-radius: 1rem;
  /* 16px */
}

.search-filters-refresh {
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
}
</style>
